import {useState} from 'react';
import {useSelector} from 'react-redux';
import {ChevronDown, Play} from 'lucide-react';
import {PhoneHQSwitch} from 'ch3-ui-lib';
import {useAppDispatch} from '../../store/store';
import {
    getAutoGainControl,
    getSelectedMicrophone,
    getSelectedSpeaker,
    setAutoGainControl,
    setSelectedMicrophoneDevice,
    setSelectedSpeakerDevice,
} from '../../store/features/calls/dialerSlice';
import {resolveActiveDevice} from '../../components/Dialer/hooks/audioDevices';
import {useAudioDevices} from '../../components/Dialer/hooks/useAudioDevices';
import {SettingsCard, SettingsGroup, SettingsLayout, SettingsPage} from './SettingsLayout';
import {SettingsRow} from './SettingsRow';

const DeviceSelect = ({label, devices, value, onChange}: {
    label: string;
    devices: MediaDeviceInfo[];
    value?: string;
    onChange: (deviceId: string) => void;
}) => (
    <label className="relative block w-full max-w-[320px]">
        <span className="sr-only">{label}</span>
        <select
            aria-label={label}
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={devices.length === 0}
            className="h-control-md w-full cursor-pointer appearance-none truncate rounded-control border-0 bg-surface-muted pl-3 pr-9 text-callout font-medium text-ink outline-none hover:bg-line focus:ring-2 focus:ring-brand-focus disabled:cursor-not-allowed disabled:text-ink-disabled"
        >
            {devices.length === 0 && <option value="">No devices found</option>}
            {devices.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.label || 'Unnamed device'}</option>)}
        </select>
        <ChevronDown size={16} aria-hidden="true" className="!p-0 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 !text-ink-muted"/>
    </label>
);

type AudioContextWithSink = AudioContext & { setSinkId?: (id: string) => Promise<void> };

/** A short 440 Hz tone on the chosen speaker, so the user can check where sound comes out. */
async function playTestTone(deviceId?: string) {
    const ctx = new AudioContext() as AudioContextWithSink;
    if (deviceId && deviceId !== 'default' && ctx.setSinkId) {
        await ctx.setSinkId(deviceId).catch(() => undefined);
    }
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    oscillator.frequency.value = 440;
    oscillator.connect(gain).connect(ctx.destination);
    oscillator.start();
    oscillator.stop(ctx.currentTime + 0.6);
    oscillator.onended = () => ctx.close();
}

const AudioSettingsPage = () => {
    const dispatch = useAppDispatch();
    const {inputs, outputs} = useAudioDevices();
    const selectedMicrophone = useSelector(getSelectedMicrophone);
    const selectedSpeaker = useSelector(getSelectedSpeaker);
    const autoGainControl = useSelector(getAutoGainControl);
    const [playing, setPlaying] = useState(false);

    const activeMicrophone = resolveActiveDevice(inputs, selectedMicrophone);
    const activeSpeaker = resolveActiveDevice(outputs, selectedSpeaker);

    const test = async () => {
        setPlaying(true);
        try {
            await playTestTone(activeSpeaker);
        } finally {
            window.setTimeout(() => setPlaying(false), 700);
        }
    };

    return (
        <SettingsLayout title="Audio">
            <SettingsPage>
                <SettingsGroup title="Microphone" description="Choose where your voice is picked up.">
                    <SettingsCard>
                        <SettingsRow
                            title="Input device"
                            trailing={<DeviceSelect label="Input device" devices={inputs} value={activeMicrophone} onChange={(id) => dispatch(setSelectedMicrophoneDevice(id))}/>}
                        />
                        <SettingsRow
                            title="Adjust automatically"
                            description="Keeps your voice at a steady level"
                            trailing={<PhoneHQSwitch checked={autoGainControl} onCheckedChange={() => dispatch(setAutoGainControl(!autoGainControl))}/>}
                        />
                    </SettingsCard>
                </SettingsGroup>

                <SettingsGroup title="Speaker" description="Where you hear the other side.">
                    <SettingsCard>
                        <SettingsRow
                            title="Output device"
                            trailing={<DeviceSelect label="Output device" devices={outputs} value={activeSpeaker} onChange={(id) => dispatch(setSelectedSpeakerDevice(id))}/>}
                        />
                        <SettingsRow
                            title="Test"
                            description="You should hear a short tone."
                            trailing={
                                <button type="button" onClick={test} disabled={playing}
                                        className="inline-flex h-control-md cursor-pointer items-center gap-2 rounded-control bg-surface-muted px-3 text-callout font-medium text-ink hover:bg-line disabled:text-ink-disabled">
                                    <Play size={16} aria-hidden="true" className="!p-0 !text-current"/>
                                    Play test sound
                                </button>
                            }
                        />
                    </SettingsCard>
                    <p className="m-0 text-footnote text-ink-muted">During a call, open the Speaker menu to switch devices without leaving the call.</p>
                </SettingsGroup>
            </SettingsPage>
        </SettingsLayout>
    );
};

export default AudioSettingsPage;
